import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CustomFieldType } from '@ee/common/enums';
import { formatValidators, initialEvictionReasons } from '@ee/common/forms';
import { LayoutItem } from '@ee/common/models';
import { COMPOSITE_PARTS } from './composites';
import { FieldDefinitionView, LayoutPageView, TENANT_FIELD_PREFIX, isTenantField } from './intake-layout.model';
import { NO_RULES, RuleOutcome } from './layout-rule-evaluation';

/**
 * The form a client fills in, built from what the firm authored.
 *
 * Pure, and separate from the renderer on purpose. The renderer decides what a control *looks* like;
 * this decides what exists, what it is called, and what makes it valid — and those are the parts that
 * have to be identical between the wizard, a resumed draft and a submission. Building the group inside
 * the renderer would tie the shape of the data to the order things happened to be drawn in.
 *
 * The v1 wizard has none of this: `IntakeEvictionFormService` builds a fixed group per step, so what a
 * firm authored could not change which controls existed. That is the same defect as the hard-coded
 * steps, one level down.
 */

/** The placement key. The submitted case also carries `eviction_reasons`; that split happens at submit. */
const EVICTION_REASON_KEY = 'eviction_reason';

/**
 * What a new answer starts as.
 *
 * Only the eviction reason has a product default (Unpaid Rent, unless the firm set another). A radio
 * holds one value; checkboxes hold the list. Every other control starts blank — a default here would
 * file an answer the client never saw.
 */
function startingValue(key: string, definition: FieldDefinitionView | undefined): unknown {
  if (key !== EVICTION_REASON_KEY || !definition?.field) {
    return null;
  }
  const reasons = initialEvictionReasons(definition.field);
  return definition.field.type === CustomFieldType.MULTI_SELECT ? reasons : (reasons[0] ?? null);
}

/** A control's name is its storage key. Tenant fields drop the prefix — they live inside their own group. */
export function controlNameFor(fieldKey: string): string {
  return isTenantField(fieldKey) ? fieldKey.slice(TENANT_FIELD_PREFIX.length) : fieldKey;
}

/**
 * Every placement on the form, in page order, excluding content blocks.
 *
 * Blocks are headings, help text and dividers: they are drawn and never answered, so a control for one
 * would be a value nothing could ever set.
 */
export function placementsOf(pages: LayoutPageView[]): LayoutItem[] {
  return pages
    .flatMap((page) => page.sections)
    .flatMap((section) => section.rows)
    .flatMap((row) => row.items)
    .filter((item) => item.kind === 'FIELD' || item.kind === 'COMPOSITE');
}

/**
 * What makes one answer valid.
 *
 * Required comes from the *placement*, not the definition: the same field can be required on the intake
 * form and optional on a workflow step, which is the whole reason `required_intake` sits where it does.
 */
export function validatorsFor(item: LayoutItem, definition: FieldDefinitionView | undefined): ValidatorFn[] {
  const validators: ValidatorFn[] = [];
  if (item.required_intake === true || item.lock_reason === 'ALWAYS_REQUIRED') {
    validators.push(Validators.required);
  }
  switch (definition?.type) {
    case CustomFieldType.DECIMAL:
    case CustomFieldType.INTEGER:
      // Not `Validators.pattern` on a number input: a browser number field already refuses letters, and
      // a second rule here would reject the empty string a cleared optional field leaves behind.
      validators.push(Validators.min(0));
      break;
    case CustomFieldType.TEXT:
      // A firm's EMAIL / URL format, as the case form applies it (`buildCaseForm`).
      validators.push(...formatValidators(definition.field?.format));
      break;
    default:
      break;
  }
  return validators;
}

/**
 * The tenant party, which repeats.
 *
 * One group per tenant, holding whichever tenant fields the firm placed. A `FormArray` rather than
 * numbered controls because the count is the client's to decide — "Add another tenant" is the whole
 * point of the section — and a fixed set of `tenant_1_*` controls cannot express it.
 */
export function tenantGroup(pages: LayoutPageView[], definitions: Map<string, FieldDefinitionView>): FormGroup {
  const group = new FormGroup({});
  placementsOf(pages)
    .filter((item) => isTenantField(item.field_key))
    .forEach((item) => {
      const key = item.field_key ?? '';
      const name = controlNameFor(key);
      if (!group.contains(name)) {
        group.addControl(name, new FormControl<unknown>(null, validatorsFor(item, definitions.get(key))));
      }
    });
  return group;
}

/**
 * Build the whole form.
 *
 * Composites contribute a control **per part**, not one for the block: the block is one question to a
 * client but the answers land in seven separate places, and every other surface — documents, the
 * sidebar, imports, the API — addresses those seven. A part that is derived gets a control too, so the
 * autocomplete has somewhere to write the county it resolved.
 */
export function buildIntakeForm(pages: LayoutPageView[], definitions: Map<string, FieldDefinitionView>): FormGroup {
  const form = new FormGroup({});

  placementsOf(pages).forEach((item) => {
    if (isTenantField(item.field_key)) {
      return;
    }
    if (item.kind === 'COMPOSITE' && item.composite_type) {
      (COMPOSITE_PARTS[item.composite_type] ?? []).forEach((part) => {
        if (form.contains(part.key)) {
          return;
        }
        // A derived part is never asked, so it is never required — the value arrives from the
        // autocomplete or the ZIP lookup, and requiring it would block a form nobody can complete.
        // An optional part keeps its type's checks and drops only the composite's requiredness.
        const validators = part.derived
          ? []
          : validatorsFor(item, definitions.get(part.key)).filter(v => !(part.optional && v === Validators.required));
        form.addControl(part.key, new FormControl<unknown>(null, validators));
      });
      return;
    }
    const key = item.field_key;
    if (!key || form.contains(key)) {
      return;
    }
    form.addControl(
      key,
      new FormControl<unknown>(startingValue(key, definitions.get(key)), validatorsFor(item, definitions.get(key)))
    );
  });

  const tenants = tenantGroup(pages, definitions);
  if (Object.keys(tenants.controls).length) {
    // At least one tenant always exists: a form asking who is being evicted with no row to answer in
    // reads as broken, and "Add another tenant" is an addition to something.
    form.addControl('tenants', new FormArray([tenants]));
  }

  return form;
}

/** Add a tenant. The caller owns the array; this keeps the shape in one place. */
export function addTenant(
  form: FormGroup,
  pages: LayoutPageView[],
  definitions: Map<string, FieldDefinitionView>
): void {
  const tenants = form.get('tenants') as FormArray | null;
  tenants?.push(tenantGroup(pages, definitions));
}

/**
 * Which controls a page owns, for per-step validation.
 *
 * A stepper has to know whether *this* step is answered, not whether the form is — otherwise Continue
 * is blocked on page two by something unanswered on page four, with nothing on screen to explain it.
 */
export function controlNamesOnPage(page: LayoutPageView): string[] {
  const names = new Set<string>();
  page.sections
    .flatMap((section) => section.rows)
    .flatMap((row) => row.items)
    .forEach((item) => {
      if (item.kind === 'COMPOSITE' && item.composite_type) {
        (COMPOSITE_PARTS[item.composite_type] ?? []).forEach((part) => names.add(part.key));
        return;
      }
      if (item.kind !== 'FIELD' || !item.field_key) {
        return;
      }
      names.add(isTenantField(item.field_key) ? 'tenants' : item.field_key);
    });
  return [...names];
}

/** Whether a rule on `tenants.<name>`, or on the whole tenant placement, is in one of the outcome's sets. */
function tenantRuleIn(targets: ReadonlySet<string>, name: string): boolean {
  return targets.has(`${TENANT_FIELD_PREFIX}${name}`) || targets.has('tenants');
}

/**
 * A tenant field no row is asked: a rule hides or disables it. Continue and Review both read this, so
 * the two cannot disagree about a tenant field.
 */
export function tenantFieldNotAsked(name: string, rules: RuleOutcome): boolean {
  return tenantRuleIn(rules.hidden, name) || tenantRuleIn(rules.disabled, name);
}

/** A tenant field a rule makes optional on every row, whatever its placement or a REQUIRE says. */
export function tenantFieldRelaxed(name: string, rules: RuleOutcome): boolean {
  return tenantRuleIn(rules.relaxed, name);
}

/** Whether a value counts as an answer: not null, not blank text, not an empty list. */
export function isAnswered(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/**
 * Valid, or invalid only for being blank when a rule has made blank acceptable.
 *
 * `requiredByRule` is a REQUIRE rule's say: it installs no validator (the form's validators come from
 * the placement), so a blank it requires is refused here — unless OPTIONAL relaxes it, as on Review.
 */
function answeredAcceptably(control: AbstractControl | null, relaxed: boolean, requiredByRule = false): boolean {
  if (!control || control.disabled) {
    return true;
  }
  if (requiredByRule && !relaxed && !isAnswered(control.value)) {
    return false;
  }
  if (control.valid) {
    return true;
  }
  const errors = Object.keys(control.errors ?? {});
  return relaxed && errors.length > 0 && errors.every((error) => error === 'required');
}

/**
 * Whether everything this page asks for has been answered acceptably.
 *
 * Under the rules, not only the placement: a field a rule hides or disables cannot be answered, and one
 * it stops requiring is not being asked for, so none of them may strand a client on the page. A relaxed
 * field still has to be well-formed if it is answered.
 *
 * Tenant fields are checked per row and per field, because that is where the rules land
 * (`tenants.<field>`): checked as one array, no rule on a tenant field could ever reach Continue.
 */
export function pageIsValid(form: FormGroup, page: LayoutPageView, rules: RuleOutcome = NO_RULES): boolean {
  const asked: LayoutPageView = {
    ...page,
    sections: page.sections.filter((section) => !rules.hidden.has(section.key))
  };
  return controlNamesOnPage(asked).every((name) => {
    if (rules.hidden.has(name)) {
      return true;
    }
    const control = form.get(name);
    if (name === 'tenants' && control instanceof FormArray) {
      return control.disabled || tenantRowsAnswered(control, asked, rules);
    }
    return answeredAcceptably(control, rules.relaxed.has(name), rules.required.has(name));
  });
}

function tenantRowsAnswered(tenants: FormArray, page: LayoutPageView, rules: RuleOutcome): boolean {
  const names = placementsOf([page])
    .filter((item) => isTenantField(item.field_key))
    .map((item) => controlNameFor(item.field_key ?? ''))
    .filter((name) => !tenantFieldNotAsked(name, rules));
  return tenants.controls.every((tenant) =>
    names.every((name) =>
      answeredAcceptably(
        tenant.get(name),
        tenantFieldRelaxed(name, rules),
        rules.required.has(`${TENANT_FIELD_PREFIX}${name}`)
      )
    )
  );
}

const NONE: ReadonlySet<string> = new Set();

/** Composite parts a section-wide REQUIRE does not reach: `optional` (unit type/number) and `derived`. */
const NEVER_REQUIRED_PARTS: ReadonlySet<string> = new Set(
  Object.values(COMPOSITE_PARTS)
    .flat()
    .filter((part) => part.optional || part.derived)
    .map((part) => part.key)
);

/**
 * The outcome with every section target spelled out as the fields placed in it.
 *
 * A rule can target a section; the server applies REQUIRE, OPTIONAL, DISABLE and HIDE to every field
 * placed in it (a tenant placement as `tenants`), so the wizard must too. Left as the section key, Review
 * and Continue look each field up by its own key and never see the rule.
 */
export function withSectionTargets(outcome: RuleOutcome, pages: LayoutPageView[]): RuleOutcome {
  const sections = pages.flatMap((page) => page.sections.map((section) => ({ ...page, sections: [section] })));
  const reasons = new Map(outcome.reasons);
  const expand = (targets: ReadonlySet<string>, skip: ReadonlySet<string> = NONE): Set<string> => {
    const expanded = new Set(targets);
    sections
      .filter((page) => targets.has(page.sections[0].key))
      .forEach((page) => {
        const reason = outcome.reasons.get(page.sections[0].key);
        controlNamesOnPage(page).forEach((name) => {
          if (skip.has(name)) {
            return;
          }
          expanded.add(name);
          // The firm's reason for asking the section is the reason for asking each field in it.
          if (reason && !reasons.has(name)) {
            reasons.set(name, reason);
          }
        });
      });
    return expanded;
  };
  return {
    hidden: expand(outcome.hidden),
    // Requiring a section never makes an optional or derived address part required: a house has no
    // unit, and a derived part is never asked. A rule naming the part itself still applies.
    required: expand(outcome.required, NEVER_REQUIRED_PARTS),
    reasons,
    disabled: expand(outcome.disabled),
    relaxed: expand(outcome.relaxed)
  };
}
